import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from "@angular/cdk/menu";
import { ConnectionPositionPair } from "@angular/cdk/overlay";
import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { AuthStore } from "@core/services/auth.store";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faCaretDown, faSignOut, faUser } from "@fortawesome/free-solid-svg-icons";
import { MainContainer } from "@layout/main-container/main-container";
import { Avatar } from "@ui/avatar";
import { environment } from "../../environments/environment";
import { menuItems } from "../menu";

@Component({
  selector: "sala-container",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MainContainer,
    RouterLink,
    FaIconComponent,
    Avatar,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
  ],
  template: `
    <layout-main-container
      [menuItems]="menuItems"
      [debugVariables]="environmentVariables"
      [applicationFeatures]="applicationFeatures()"
    >
      <ng-container headerRight>
        {{ user()?.fullName }}
        <button
          type="button"
          class="flex items-center rounded-full hover:bg-secondary/90"
          aria-haspopup="true"
          [cdkMenuTriggerFor]="menu"
          [cdkMenuPosition]="positions"
        >
          <ui-avatar [firstName]="user()?.firstName" [lastName]="user()?.lastName" />
          <fa-icon class="mx-1 text-secondary-foreground" [icon]="faCaretDown" />
        </button>

        <ng-template #menu>
          <div
            cdkMenu
            class="absolute top-full right-0 z-50 my-2 flex w-38 flex-col items-stretch rounded-sm border border-border bg-popover text-sm text-popover-foreground shadow-md transition-all"
          >
            @for (item of dropDownMenuLinks(); track item.label) {
              <a
                cdkMenuItem
                tabindex="0"
                class="block px-4 py-2 hover:bg-input/50"
                [routerLink]="item.route"
              >
                <fa-icon class="mr-2" [icon]="item.icon" />{{ item.label }}
              </a>
            }

            <button
              cdkMenuItem
              type="button"
              tabindex="0"
              class="flex px-4 py-2 hover:bg-input/50"
              (keydown.enter)="logout({})"
              (click)="logout({})"
            >
              <fa-icon class="mr-2" [icon]="faSignOut" />
              <span i18n>Sign out</span>
            </button>
          </div>
        </ng-template>
      </ng-container>
    </layout-main-container>
  `,
})
export class Container {
  // Dependencies
  readonly #authStore = inject(AuthStore);

  // Variables
  readonly faCaretDown = faCaretDown;
  readonly faSignOut = faSignOut;
  readonly menuItems = menuItems;
  readonly environmentVariables = environment.production
    ? []
    : [environment.appVersion];
  readonly logout = this.#authStore.logout;
  readonly positions = [
    new ConnectionPositionPair(
      { originX: "end", originY: "bottom" },
      { overlayX: "end", overlayY: "top" },
    ),
  ];

  // Signals
  readonly dropDownMenuLinks = signal([
    { label: $localize`My profile`, icon: faUser, route: "/profile" },
  ]);

  // Computeds
  readonly applicationFeatures = this.#authStore.applicationFeatures;
  readonly user = this.#authStore.user;
}
